(() => {
const asQuickOptions = window.asQuickOptions || (window.asQuickOptions = (() => {
  const entries = new Map();
  const freshFor = 5000;
  function key(url) {
    const parsed = new URL(url, location.origin);
    if (parsed.origin !== location.origin) throw new Error('Invalid product URL');
    parsed.searchParams.set('section_id', 'as-quick-options');
    return parsed.href;
  }
  function peek(url) {
    const entry = entries.get(key(url));
    return entry?.html && Date.now() - entry.time < 60000 ? entry : null;
  }
  function request(url) {
    const address = key(url);
    const previous = entries.get(address);
    if (previous?.pending) return previous.pending;
    if (previous?.html && !previous.used && Date.now() - previous.time < freshFor) return Promise.resolve(previous);
    const entry = { html: previous?.html, time: previous?.time || 0, used: false };
    entries.set(address, entry);
    entry.pending = fetch(address, { credentials: 'same-origin', cache: 'no-store' })
      .then(response => { if (!response.ok) throw new Error('Options unavailable'); return response.text(); })
      .then(html => {
        if (!html.includes('data-as-quick-options')) throw new Error('Options missing');
        entry.html = html;
        entry.time = Date.now();
        entry.pending = null;
        while (entries.size > 8) entries.delete(entries.keys().next().value);
        return entry;
      }).catch(error => { if (entries.get(address) === entry) entries.delete(address); throw error; });
    return entry.pending;
  }
  function prepare(event) {
    if (navigator.connection?.saveData || /(^|-)2g$/.test(navigator.connection?.effectiveType || '')) return;
    const button = event.target.closest?.('modal-opener[data-modal^="#QuickAdd-"] [data-product-url]');
    if (!button || button.contains(event.relatedTarget)) return;
    request(button.dataset.productUrl).catch(() => {});
  }
  document.addEventListener('pointerover', prepare, { passive: true });
  document.addEventListener('focusin', prepare);
  document.addEventListener('touchstart', prepare, { passive: true });
  if (typeof subscribe === 'function') subscribe(PUB_SUB_EVENTS.cartUpdate, () => entries.clear());
  return { request, peek };
})());

if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
          const originalUpdateMedia = target.updateMedia.bind(target);
          target.updateMedia = (html, mediaId) => {
            const source = html.querySelector('.as-quick-image');
            const destination = target.querySelector('.as-quick-image');
            if (source && destination) destination.innerHTML = source.innerHTML;
            else originalUpdateMedia(html, mediaId);
          };
        });
      }

      hide(preventFocus = false) {
        this.asOpenToken = (this.asOpenToken || 0) + 1;
        this.modalContent.querySelector("product-info")?.abortController?.abort();
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      async show(opener) {
        const token = this.asOpenToken = (this.asOpenToken || 0) + 1;
        const url = opener.getAttribute('data-product-url');
        const cached = asQuickOptions.peek(url);
        opener.setAttribute('aria-disabled', 'true');
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner')?.classList.remove('hidden');
        const render = (html, pending) => {
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const product = parsed.querySelector('product-info');
          if (!product) throw new Error('Product options missing');
          this.preprocessHTML(product);
          if (pending) {
            product.setAttribute('inert', '');
            product.setAttribute('aria-busy', 'true');
          }
          HTMLUpdateUtility.setInnerHTML(this.modalContent, product.outerHTML);
        };
        try {
          if (cached) {
            render(cached.html, true);
            super.show(opener);
          }
          const entry = await asQuickOptions.request(url);
          if (token !== this.asOpenToken) return;
          entry.used = true;
          render(entry.html, false);
          window.Shopify?.PaymentButton?.init();
          if (!this.hasAttribute('open')) super.show(opener);
        } catch (error) {
          if (token === this.asOpenToken) {
            console.warn('Quick options unavailable:', error);
            this.hide();
            window.location.assign(url);
          }
        } finally {
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          opener.querySelector('.loading__spinner')?.classList.add('hidden');
        }
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}


})();
